import styled from '@emotion/styled';
import { useCallback, useEffect, useReducer, useState } from 'react';
import { createPortal } from 'react-dom';

const Frame = styled.iframe`
  max-width: 100%;
`;

interface IFrameProps {
  children: (frameWindow: Window) => JSX.Element | null;
  height: number;
  width: number | string;
  iframeId?: string;
  isEmbedded: boolean;
  showCustomFrame?: boolean;
}

/* The IFrame component allows rendering React children inside an <iframe> element.
 * React Portal is used to change the rendering context to iframe's body node.
 * Iframe's `ref` is stored in state (rather than using a `useRef` hook) to ensure
 * an immediate re-render when the ref is set.
 */
export const IFrame = ({
  children,
  width,
  height,
  iframeId,
  isEmbedded,
  showCustomFrame = false,
}: IFrameProps): JSX.Element => {
  const [frame, setFrame] = useState<HTMLIFrameElement>();
  const [frameBody, setFrameBody] = useState<HTMLElement>();
  const [contentWindow, setContentWindow] = useState<Window | null>();

  const frameRef = useCallback((iframe: HTMLIFrameElement) => {
    setFrame(iframe);
    setFrameBody(iframe?.contentDocument?.body);
    setContentWindow(iframe?.contentWindow);
  }, []);

  const [shouldLoadContent, loadContent] = useReducer(() => true, false);

  useEffect(() => {
    if (!frame || !contentWindow || !(isEmbedded && showCustomFrame)) return undefined;

    const resizeIframe = () => {
      const { scrollHeight, scrollWidth } = contentWindow.document.body;

      if (scrollHeight > 0 && scrollWidth > 0) {
        frame.height = `${scrollHeight + 1}`;
        frame.width = `${scrollWidth}`;
      }
    };

    const observer = new ResizeObserver(resizeIframe);

    observer.observe(contentWindow.document.body);

    resizeIframe();

    return () => {
      observer.disconnect();
    };
  });

  return (
    <Frame
      title="Widget preview"
      width={width}
      height={height}
      frameBorder="0"
      ref={frameRef}
      onLoad={loadContent}
      // without this the onLoad event won't trigger on Safari
      srcDoc=""
      id={iframeId}
    >
      {shouldLoadContent &&
        frameBody &&
        contentWindow &&
        createPortal(children(contentWindow), frameBody)}
    </Frame>
  );
};
